import * as React from 'react';
import Box from '@mui/material/Box';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { PAGES } from '../../constants/pages';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { useAppSelector } from '../../store/hooks/useAppSelector';
import { RootState } from '../../store/types/rootState';
import { useIsMobileView } from '../../utils/responsive';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/hooks/useAppDispatch';
import { logoutAction } from '../../store/slices/auth';
import { saveUserAction } from '../../store/slices/user';
import { Logout, Menu } from '@mui/icons-material';

const styles = {
  root: {
    width: '100%',
    position: 'fixed',
    bottom: 0,
    zIndex: 2,
  },
  iconStyle: {
    color: 'white',
  },
  selectedIconStyle: {
    color: 'lightblue !important',
  },
  drawer: {
    '& .MuiDrawer-paper': {
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      maxHeight: '80vh',
    },
  },
  drawerHeader: {
    padding: 2,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  drawerHandle: {
    width: 40,
    height: 4,
    backgroundColor: '#ccc',
    borderRadius: 2,
    margin: '8px auto',
  },
  listItem: {
    '&.Mui-selected': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
      '& .MuiListItemIcon-root': {
        color: '#1976d2',
      },
      '& .MuiListItemText-primary': {
        color: '#1976d2',
        fontWeight: 600,
      },
    },
  },
};

export default function MobileBottomNavigation() {
  const [value, setValue] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const isLoggedIn = useAppSelector(
    ({ authentication }: RootState) => authentication.isLoggedIn,
  );

  const profile = useSelector(({ user }: RootState) => user.profile);

  const isMobile = useIsMobileView();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const logout = () => {
    dispatch(
      logoutAction({
        isLoggedIn: false,
        token: "",
      })
    );
    dispatch(saveUserAction(null));
    navigate("/login");
    setDrawerOpen(false);
  };
  
  const location = useLocation();

  
  const filteredPages = React.useMemo(
    () =>
      PAGES.filter(
        page =>
          page.availableProfiles.includes(profile) && page.renderOnMobile,
      ),
    [profile],
  );

  React.useEffect(() => {
    const activePageIndex = filteredPages.findIndex(
      page => page.url === location.pathname,
    );
    if (activePageIndex !== -1) {
      setValue(activePageIndex);
    }
  }, [filteredPages, location.pathname]);

  const handleNavigation = (url: string) => {
    navigate(url);
    setDrawerOpen(false);
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  return isLoggedIn && isMobile ? (
    <>
      <Box sx={styles.root}>
        <BottomNavigation
          showLabels
          value={value}
          onChange={(event, newValue) => {
            setValue(newValue);
          }}
          sx={{
            backgroundColor: '#000',
          }}
        >
          <BottomNavigationAction
            key="menu"
            icon={<Menu />}
            label="Menú"
            sx={styles.iconStyle}
            onClick={toggleDrawer(true)}
          />
          {filteredPages.slice(0, 3).map((page, index) => (
            <BottomNavigationAction
              key={page.name}
              icon={page.icon}
              sx={value === index ? styles.selectedIconStyle : styles.iconStyle}
              onClick={() => navigate(page.url)}
            />
          ))}
        </BottomNavigation>
      </Box>

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={styles.drawer}
      >
        <Box sx={styles.drawerHeader}>
          <Box sx={styles.drawerHandle} />
        </Box>
        <List sx={{ paddingTop: 0 }}>
          {filteredPages.map((page, index) => {
            const isActive = location.pathname === page.url;
            return (
              <ListItem
                key={page.name}
                disablePadding
                sx={{
                  ...styles.listItem,
                  ...(isActive && {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    '& .MuiListItemIcon-root': {
                      color: '#1976d2',
                    },
                    '& .MuiListItemText-primary': {
                      color: '#1976d2',
                      fontWeight: 600,
                    },
                  }),
                }}
              >
                <ListItemButton onClick={() => handleNavigation(page.url)}>
                  <ListItemIcon sx={{ color: isActive ? '#1976d2' : 'inherit', minWidth: 40 }}>
                    {page.icon}
                  </ListItemIcon>
                  <ListItemText primary={page.name} />
                </ListItemButton>
              </ListItem>
            );
          })}
          <Divider />
          <ListItem disablePadding>
            <ListItemButton onClick={logout}>
              <ListItemIcon sx={{ minWidth: 40, color: '#d32f2f' }}>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Cerrar Sesión" sx={{ color: '#d32f2f' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  ) : <></>;
}
